import type { Scraper } from "agent-twitter-client";
import { promises as fs } from "fs";
import { existsSync } from "fs";
import type { Tweet, TweetReply, EnhancedSuspiciousActivityReport } from "@podx/core/types";
import { config, logger, errors } from "@podx/core";
import { TokenShillMonitorService } from "./TokenShillMonitorService.js";
import { SuspiciousActivityMonitor } from "../analyzers/SuspiciousActivityMonitor.js";

export interface ScrapingProgress {
  count: number;
  max: number;
}

export interface ScrapingOptions {
  targetUsername: string;
  maxTweets: number;
  progressCallback?: (progress: ScrapingProgress) => void;
  includeBotAnalysis?: boolean;
  includeTokenAnalysis?: boolean;
  analyzeReplies?: boolean;
}

export class ScraperService {
  private scraper: any;
  private tokenMonitorService: TokenShillMonitorService;
  private activityMonitor: SuspiciousActivityMonitor;

  constructor() {
    // Import Scraper class dynamically to avoid namespace issues
    const { Scraper } = require("agent-twitter-client");
    this.scraper = new Scraper();
    this.tokenMonitorService = new TokenShillMonitorService();
    this.activityMonitor = new SuspiciousActivityMonitor();
  }

  private async authenticate(): Promise<void> {
    const { username, password, email } = config.x;

    if (!username || !password) {
      throw errors.authentication("Twitter credentials not configured", {
        operation: "login",
        missing: {
          username: !username,
          password: !password
        }
      });
    }

    logger.info("Authenticating with X/Twitter", { operation: "authentication" });
    await this.scraper.login(username, password, email);
    logger.info("Successfully authenticated with X/Twitter", { operation: "authentication" });
  }

  async withScraper<T>(task: (scraper: any) => Promise<T>): Promise<T> {
    try {
      await this.authenticate();
      return await task(this.scraper);
    } catch (error) {
      logger.warn("Scraper operation failed", { operation: "scraper_task", error: (error as Error).message });
      throw error;
    }
  }

  async scrapeAccount(options: ScrapingOptions): Promise<Tweet[]> {
    const { targetUsername, maxTweets, progressCallback } = options;
    const tweets: Tweet[] = [];

    return await this.withScraper(async (scraper) => {
      let count = 0;

      logger.info(`Starting scrape operation for @${targetUsername}`, {
        operation: "scrape_start",
        targetUsername,
        maxTweets
      });

      for await (const tweet of scraper.getTweets(targetUsername, maxTweets) as any) {
        tweets.push(tweet as unknown as Tweet);
        count++;

        if (progressCallback) {
          progressCallback({ count, max: maxTweets });
        }

        if (count % 50 === 0) {
          logger.info(`Scraped ${count} tweets from @${targetUsername}`, {
            operation: "scrape_progress",
            targetUsername,
            count,
            maxTweets
          });
        }
      }

      logger.info(`Completed scrape operation for @${targetUsername}`, {
        operation: "scrape_complete",
        targetUsername,
        totalTweets: tweets.length
      });

      return tweets;
    });
  }

  async saveTweetsToFile(tweets: Tweet[], targetUsername: string): Promise<string> {
    const userDir = `scraped_data/${targetUsername}`;
    if (!existsSync(userDir)) {
      await fs.mkdir(userDir, { recursive: true });
    }

    const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
    const filename = `${userDir}/tweets_${timestamp}.json`;
    await fs.writeFile(filename, JSON.stringify(tweets, null, 2));

    logger.info(`Saved tweets to file`, {
      operation: "save_tweets",
      targetUsername,
      filename,
      tweetCount: tweets.length
    });

    return filename;
  }

  async scrapeAndSave(options: ScrapingOptions): Promise<{ 
    tweets: Tweet[]; 
    filename: string; 
    analysis?: EnhancedSuspiciousActivityReport;
    analysisFilename?: string;
  }> {
    const tweets = await this.scrapeAccount(options);
    const filename = await this.saveTweetsToFile(tweets, options.targetUsername);
    
    let analysis: EnhancedSuspiciousActivityReport | undefined;
    let analysisFilename: string | undefined;

    // Perform analysis if requested
    if (options.includeBotAnalysis || options.includeTokenAnalysis || options.analyzeReplies) {
      analysis = await this.performComprehensiveAnalysis(tweets, options);
      analysisFilename = await this.saveAnalysisReport(analysis, options.targetUsername);
    }
    
    return { tweets, filename, analysis, analysisFilename };
  }

  private async performComprehensiveAnalysis(
    tweets: Tweet[], 
    options: ScrapingOptions
  ): Promise<EnhancedSuspiciousActivityReport> {
    logger.info("Starting comprehensive analysis", {
      operation: "comprehensive_analysis",
      targetUsername: options.targetUsername,
      tweetCount: tweets.length,
      includeBotAnalysis: options.includeBotAnalysis,
      includeTokenAnalysis: options.includeTokenAnalysis,
      analyzeReplies: options.analyzeReplies
    });

    // Get replies if requested
    let replies: TweetReply[] = [];
    if (options.analyzeReplies) {
      replies = await this.scrapeRepliesForTweets(tweets);
    }

    // Perform bot and activity analysis
    let suspiciousActivityReport = this.activityMonitor.generateReport([]);
    if (options.includeBotAnalysis && replies.length > 0) {
      suspiciousActivityReport = this.activityMonitor.generateReport(replies);
    }

    // Perform token shilling analysis
    let enhancedReport: EnhancedSuspiciousActivityReport;
    if (options.includeTokenAnalysis) {
      enhancedReport = await this.tokenMonitorService.analyzeTokenShilling(replies);
    } else {
      // Convert regular report to enhanced report format
      enhancedReport = {
        ...suspiciousActivityReport,
        tokenShilling: {
          suspiciousTokens: [],
          activeShillCampaigns: 0,
          botAccountsTracked: [],
          alerts: []
        },
        pumpDumpAnalysis: {
          activeCampaigns: [],
          riskTokens: [],
          coordinatedNetworks: 0,
          timeToAction: []
        }
      };
    }

    logger.info("Completed comprehensive analysis", {
      operation: "comprehensive_analysis_complete",
      targetUsername: options.targetUsername,
      suspiciousReplies: enhancedReport.summary.suspiciousReplies,
      botProbability: enhancedReport.summary.botProbability,
      activeShillCampaigns: enhancedReport.tokenShilling.activeShillCampaigns,
      alertsGenerated: enhancedReport.tokenShilling.alerts.length
    });

    return enhancedReport;
  }

  private async scrapeRepliesForTweets(tweets: Tweet[]): Promise<TweetReply[]> {
    const replies: TweetReply[] = [];
    
    logger.info("Scraping replies for tweets", {
      operation: "scrape_replies",
      tweetCount: tweets.length
    });

    return await this.withScraper(async (scraper) => {
      for (const tweet of tweets.slice(0, 10)) { // Limit to first 10 tweets to avoid rate limits
        try {
          const tweetReplies = await scraper.getTweetReplies(tweet.id);
          
          for await (const reply of tweetReplies) {
            replies.push({
              id: reply.id,
              text: reply.text || reply.full_text || '',
              createdAt: reply.created_at || reply.createdAt || new Date().toISOString(),
              username: reply.username || reply.user?.screen_name || '',
              name: reply.name || reply.user?.name || '',
              likeCount: reply.favorite_count || reply.likeCount || 0,
              retweetCount: reply.retweet_count || reply.retweetCount || 0,
              parentTweetId: tweet.id
            });
          }
        } catch (error) {
          logger.warn("Failed to scrape replies for tweet", {
            operation: "scrape_replies_error",
            tweetId: tweet.id,
            error: (error as Error).message
          });
        }
      }

      logger.info("Completed scraping replies", {
        operation: "scrape_replies_complete",
        totalReplies: replies.length
      });

      return replies;
    });
  }

  private async saveAnalysisReport(analysis: EnhancedSuspiciousActivityReport, targetUsername: string): Promise<string> {
    const userDir = `scraped_data/${targetUsername}`;
    if (!existsSync(userDir)) {
      await fs.mkdir(userDir, { recursive: true });
    }

    const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
    const filename = `${userDir}/analysis_${timestamp}.json`;
    await fs.writeFile(filename, JSON.stringify(analysis, null, 2));

    logger.info("Saved analysis report to file", {
      operation: "save_analysis",
      targetUsername,
      filename,
      suspiciousReplies: analysis.summary.suspiciousReplies,
      alerts: analysis.tokenShilling.alerts.length
    });

    return filename;
  }
}

// Export singleton instance
export const scraperService: ScraperService = new ScraperService();

// Export convenience function for backward compatibility
export async function scrapeAccount(
    targetUsername: string, 
    maxTweets: number, 
    progressCallback?: (progress: { count: number; max: number }) => void
): Promise<Tweet[]> {
    const opts: ScrapingOptions = { targetUsername, maxTweets };
    if (progressCallback) {
        opts.progressCallback = (p) => progressCallback({ count: p.count, max: p.max });
    }
    const { tweets } = await scraperService.scrapeAndSave(opts);
    
    return tweets;
}

// Enhanced scraping function with bot detection and token analysis
export async function scrapeWithAnalysis(
    targetUsername: string,
    maxTweets: number,
    options?: {
      includeBotAnalysis?: boolean;
      includeTokenAnalysis?: boolean;
      analyzeReplies?: boolean;
      progressCallback?: (progress: { count: number; max: number }) => void;
    }
): Promise<{
    tweets: Tweet[];
    filename: string;
    analysis?: EnhancedSuspiciousActivityReport;
    analysisFilename?: string;
}> {
    const opts: ScrapingOptions = {
        targetUsername,
        maxTweets,
        includeBotAnalysis: options?.includeBotAnalysis ?? config.analysis.enableBotDetection,
        includeTokenAnalysis: options?.includeTokenAnalysis ?? true,
        analyzeReplies: options?.analyzeReplies ?? config.analysis.enableReplyAnalysis,
    };
    
    if (options?.progressCallback) {
        opts.progressCallback = (p) => options.progressCallback!({ count: p.count, max: p.max });
    }
    
    return await scraperService.scrapeAndSave(opts);
}

// Export scrapeProfile function for backward compatibility
export async function scrapeProfile(username: string): Promise<any> {
    return await scraperService.withScraper(async (scraper) => {
        const profile = await scraper.getProfile(username);
        
        // Save profile to file
        const profilesDir = `scraped_data/profiles`;
        if (!existsSync(profilesDir)) {
            await fs.mkdir(profilesDir, { recursive: true });
        }
        
        const filename = `${profilesDir}/${username}.json`;
        await fs.writeFile(filename, JSON.stringify(profile, null, 2));
        
        logger.info(`Saved profile to file`, {
            operation: "save_profile",
            username,
            filename
        });
        
        return profile;
    });
}
